import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import FooterWithNewsletter from "@/components/ui/footer-column";
import SEO from "@/components/SEO";
import { useSEO } from "@/hooks/useSEO";
import { getSEOConfigForPage } from "@/utils/seoUtils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Marquee } from "@/components/ui/marquee";
import { ChevronLeft, ChevronRight, Hash, Search } from "lucide-react";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import LinkedInSubscribeButton from "@/components/ui/linkedin-subscribe-button";
import { getLaughsGenerated } from "@/utils/laughsCalculator";
import { getPublicImage } from "@/utils/imageUtils";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ShareSidebar } from "@/components/ui/share-sidebar";
import Configs from "@/config/Configs";

import dailyCircusBotImage from "@/assets/daily-circus-bot.jpg";

// API URL setup like TellAFriendForm
let apiUrl = "https://api.gimmefy.ai/v2";

const baseUrl = window.location.origin;
if (baseUrl.includes("localhost") || baseUrl.includes("dev.gimmefy.ai")) {
  apiUrl = "https://apidev.gimmefy.ai/v2";
} else if (baseUrl.includes("staging.gimmefy.ai")) {
  apiUrl = "https://apistaging.gimmefy.ai/v2";
}

interface Comic {
  id: number;
  title: string;
  date: string;
  episode: number;
  imageUrl: string;
  theme: string;
  description: string;
  readMoreUrl?: string;
  slug?: string;
}

// Mock data for comics - Episodes 1-14 only
const mockComics: Comic[] = [
  {
    id: 14,
    title: "Are you going (Nano) bananas yet?",
    date: "2024-01-15",
    episode: 14,
    imageUrl: "/lovable-uploads/daily-circus-14.jpg",
    theme: "AI Innovation",
    description: "\"Nano Banana\" has become the latest AI buzzword, and for all the right reasons. Google's Gemini 2.5 Flash Image was too wordy, and too boring, so it's been nicknamed after a small fruit; go figure. It's true power is that It lets users transform photos with natural language prompts, offering unmatched consistency in character editing and scene preservation. 3 words: Fast, Precise, Powerful.",
    readMoreUrl: "https://www.glbgpt.com/hub/what-exactly-is-nano-banana-and-how-it-went-viral-across-the-internet/"
  },
  // Episodes 1-13
  ...Array.from({ length: 13 }, (_, i) => ({
    id: 13 - i,
    title: `Comic Episode ${13 - i}`,
    date: `2024-01-${String(14 - i).padStart(2, '0')}`,
    episode: 13 - i,
    imageUrl: "/lovable-uploads/daily-circus-14.jpg", // Using same image for demo
    theme: i % 3 === 0 ? "AI Mishaps" : i % 3 === 1 ? "Marketing Mayhem" : "Team Dynamics",
    description: `Daily circus adventure episode ${13 - i}`
  }))
];

const ComicViewer = ({ comic, onPrevious, onNext, hasPrevious, hasNext, showEpisodeBadge = true }: { 
  comic: Comic;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  showEpisodeBadge?: boolean;
  onShare?: (comic: Comic) => void;
}) => (
  <div className="space-y-6">
    {(showEpisodeBadge || onPrevious || onNext) && (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {showEpisodeBadge && (
            <Badge variant="secondary" className="flex items-center gap-2">
              <Hash className="w-4 h-4" />
              Episode {comic.episode}
            </Badge>
          )}
        </div>
        {(onPrevious || onNext) && (
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={onPrevious}
              disabled={!hasPrevious}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={onNext}
              disabled={!hasNext}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    )}
    
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <img 
          src={comic.imageUrl} 
          alt={comic.title}
          className="w-full h-auto max-h-[600px] object-contain bg-background"
        />
      </CardContent>
    </Card>
    
    <div className="space-y-4">
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-3">{comic.title}</h2>
        <p className="text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto">
          {comic.description}
        </p>
      </div>
      
      <div className="flex items-center justify-center gap-4">
        {comic.readMoreUrl && (
          <Button 
            variant="default" 
            onClick={() => window.open(comic.readMoreUrl, '_blank', 'noopener,noreferrer')}
            className="flex items-center gap-2"
          >
            Read More About This
          </Button>
        )}
        {/* <Button 
          variant="outline" 
          className="flex items-center gap-2"
          onClick={() => onShare?.(comic)}
        >
          <Share2 className="w-4 h-4" />
          Share
        </Button> */}
      </div>
    </div>
  </div>
);

const ComicCard = ({ comic, onClick, showTeaser = false }: { comic: Comic; onClick?: () => void; showTeaser?: boolean }) => (
  <Card 
    className="group cursor-pointer transition-all duration-200 hover:shadow-lg hover:scale-105"
    onClick={onClick}
  >
    <CardContent className="p-0">
      <div className="aspect-video overflow-hidden rounded-t-lg relative">
        {showTeaser ? (
          <>
            {/* Cropped image showing only top portion */}
            <div className="w-full h-full relative overflow-hidden">
              <img 
                src={comic.imageUrl} 
                alt={comic.title}
                className="w-full h-auto object-cover object-top scale-125 group-hover:scale-[1.35] transition-transform duration-200"
                style={{ minHeight: '200%' }}
              />
            </div>
          </>
        ) : (
          <img 
            src={comic.imageUrl} 
            alt={comic.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-200"
          />
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <Badge variant="secondary">#{comic.episode}</Badge>
        </div>
        <h3 className="font-semibold mb-1">{comic.title}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2">{comic.description}</p>
        {showTeaser && (
          <div className="mt-3 bg-primary/10 border border-primary/20 rounded-lg px-4 py-2 text-center group-hover:bg-primary/20 transition-colors">
            <p className="text-sm font-medium text-primary">
              Click to read full comic →
            </p>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

const ComicGrid = ({ comics, onComicClick, showTeasers = true, onTabChange }: { 
  comics: Comic[]; 
  onComicClick?: (comic: Comic) => void; 
  showTeasers?: boolean;
  onTabChange?: (tab: string) => void;
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams] = useSearchParams();
  
  // Convert URL-friendly tag back to display format
  const tagParam = searchParams.get('tag');
  const tagFilter = tagParam ? tagParam.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()).replace('Ai ', 'AI ') : null;
  
  // Sync activeFilter with URL parameter
  const activeFilter = tagFilter || "Latest";
  

  const filteredComics = comics.filter(comic => {
    // Search filter
    const matchesSearch = comic.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         comic.episode.toString().includes(searchTerm);
    
    // Tab filter
    let matchesTab = true;
    if (activeFilter !== "Latest") {
      const comicTags = typeof comic.theme === 'string' ? comic.theme.split(',').map(tag => tag.trim()) : [comic.theme];
      matchesTab = comicTags.some(tag => tag.toLowerCase() === activeFilter.toLowerCase());
      
    }
    
    return matchesSearch && matchesTab;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <Tabs value={activeFilter} onValueChange={(value) => {
          onTabChange?.(value);
        }}>
          <TabsList>
            <TabsTrigger value="Latest">Latest</TabsTrigger>
            <TabsTrigger value="Popular">Popular</TabsTrigger>
            <TabsTrigger value="This Week">This Week</TabsTrigger>
          </TabsList>
        </Tabs>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Search comics..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-64"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredComics.map((comic) => (
          <ComicCard 
            key={comic.id} 
            comic={comic} 
            onClick={() => onComicClick?.(comic)}
            showTeaser={showTeasers}
          />
        ))}
      </div>
    </div>
  );
};

const DailyCircusPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // URL parameter handling
  const slugParam = searchParams.get('slug');
  
  const { fallbackMetadata } = getSEOConfigForPage('/daily-circus');
  const { seoData, canonicalUrl } = useSEO({ fallbackMetadata });
  
  // Convert URL-friendly tag back to display format
  const tagParam = searchParams.get('tag');
  const tagFilter = tagParam ? tagParam.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()).replace('Ai ', 'AI ') : null;
  
  
  const [selectedComic, setSelectedComic] = useState<Comic | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [laughsGenerated, setLaughsGenerated] = useState<string>('50K+');
  const [apiComics, setApiComics] = useState<Comic[]>([]);
  const [_isLoadingComics, setIsLoadingComics] = useState(true);
  
  // Use API comics if available, otherwise fall back to mock comics
  const comicsToUse = apiComics.length > 0 ? apiComics : mockComics;
  
  // Get dynamic comic data based on slug parameter
  const getDynamicComicData = () => {
    // Only return dynamic data if we have a specific slug AND the comic is found
    if (slugParam && comicsToUse.length > 0) {
      const comicBySlug = comicsToUse.find(comic => comic.slug === slugParam);
      if (comicBySlug) {
        return {
          title: comicBySlug.title,
          description: comicBySlug.description,
          imageUrl: comicBySlug.imageUrl
        };
      }
    }
    
    // Return null if no specific slug - this will use page-level SEO
    return null;
  };
  
  // Filter comics by tag if provided
  const filteredComics = tagFilter 
    ? comicsToUse.filter(comic => {
        // Handle comma-separated tags from API
        const comicTags = typeof comic.theme === 'string' ? comic.theme.split(',').map(tag => tag.trim()) : [comic.theme];
        return comicTags.some(tag => tag.toLowerCase() === tagFilter.toLowerCase());
      })
    : comicsToUse;
  
  const todaysComic = filteredComics[0];

  // Calculate laughs generated and ensure page scroll is unlocked on mount
  useEffect(() => {
    // Defensive unlock in case dialog lock persisted after navigation
    document.body.classList.remove('dialog-open');
    document.body.style.overflow = '';

    const calculateLaughs = async () => {
      try {
        const laughs = await getLaughsGenerated(filteredComics.length);
        setLaughsGenerated(laughs);
      } catch (error) {
        console.error('Failed to calculate laughs:', error);
        setLaughsGenerated('50K+');
      }
    };

    calculateLaughs();
  }, [filteredComics.length]);

  // Fetch comics from API
  useEffect(() => {
    const fetchComics = async () => {
      try {
        setIsLoadingComics(true);
        
        // Make API call to public endpoint (no authentication required)
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };

        // Build query parameters
        const params = new URLSearchParams({
          'filters[group_name]': Configs.CONTENTS.GROUP_NAME.COMIC,
          'sort': '-display_order'
        });

        const response = await fetch(`${apiUrl}/contents/public?${params}`, {
          method: 'GET',
          headers
        });

        if (!response.ok) {
          throw new Error('Failed to fetch comics');
        }

        const data = await response.json();

        // Transform API data to match our Comic interface
        if (data.payload && Array.isArray(data.payload)) {
          const transformedComics: Comic[] = data.payload.map((item: Record<string, unknown>, index: number) => {
            const title = (item.name as string) || `Comic Episode ${index + 1}`;
            // Generate slug from title
            const slug = title.toLowerCase()
              .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
              .replace(/\s+/g, '-') // Replace spaces with hyphens
              .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
              .trim();
            
            return {
              id: (item.id as number) || index + 1,
              title: title,
              date: item.created_at ? new Date(item.created_at as string).toISOString().split('T')[0] : `2024-01-${String(index + 1).padStart(2, '0')}`,
              episode: (item.display_order as number) || index + 1,
              imageUrl: (item.image as string) ? getPublicImage('frontend-files', item.image as string) || "/lovable-uploads/daily-circus-14.jpg" : "/lovable-uploads/daily-circus-14.jpg",
              theme: (item.tags as string) || (index % 3 === 0 ? "AI Mishaps" : index % 3 === 1 ? "Marketing Mayhem" : "Team Dynamics"),
              description: (item.content as string) || `Daily circus adventure episode ${index + 1}`,
              readMoreUrl: item.content2 as string,
              slug: slug
            };
          });

          setApiComics(transformedComics);
        }

      } catch (error) {
        console.error('Error fetching comics from API:', error);
        setApiComics([]);
      } finally {
        setIsLoadingComics(false);
      }
    };

    fetchComics();
  }, []);

  // Handle slug parameter to open specific comic
  useEffect(() => {
    if (slugParam && comicsToUse.length > 0) {
      const comicBySlug = comicsToUse.find(comic => {
        const episodeSlug = `comic-episode${comic.episode}`;
        return comic.slug === slugParam || episodeSlug === slugParam;
      });
      
      if (comicBySlug && (!selectedComic || selectedComic.id !== comicBySlug.id)) {
        setSelectedComic(comicBySlug);
        setIsDialogOpen(true);
      }
    }
  }, [slugParam, comicsToUse, selectedComic]);

  // Note: Scroll prevention is now handled in the DialogContent component

  const handleComicClick = (comic: Comic) => {
    setSelectedComic(comic);
    setIsDialogOpen(true);
    // Update URL to show current comic
    const newUrl = `/daily-circus?slug=${comic.slug}`;
    navigate(newUrl, { replace: true });
  };

  const handleTabChange = (tab: string) => {
    if (tab === "Latest") {
      // Clear all parameters for Latest
      navigate('/daily-circus', { replace: true });
    } else {
      // Convert tab name to URL-friendly format
      const urlFriendlyTab = tab.toLowerCase().replace(/\s+/g, '-');
      const newUrl = `/daily-circus?tag=${urlFriendlyTab}`;
      navigate(newUrl, { replace: true });
    }
  };

  const handleCloseDialog = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      // Clear URL when dialog closes, similar to ai-ad-gallery and getting-started
      navigate('/daily-circus', { replace: true });
    }
  };

  const handleShare = (comic: Comic) => {
    const shareUrl = `${window.location.origin}/daily-circus?slug=${comic.slug || `comic-episode${comic.episode}`}`;
    const shareText = `Check out this hilarious comic: ${comic.title}`;
    
    // Check if Web Share API is supported
    if (navigator.share) {
      navigator.share({
        title: comic.title,
        text: shareText,
        url: shareUrl
      }).catch(console.error);
    } else {
      // Fallback: copy to clipboard and show alert
      navigator.clipboard.writeText(shareUrl).then(() => {
        alert('Comic link copied to clipboard!');
      }).catch(() => {
        // Final fallback: open share popup
        const shareWindow = window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
          'share',
          'width=600,height=400,scrollbars=yes,resizable=yes'
        );
        if (shareWindow) {
          shareWindow.focus();
        }
      });
    }
  };

  const navigateComic = (direction: 'prev' | 'next') => {
    if (!selectedComic) return;
    
    // Find current comic index in comicsToUse (all comics, not filtered)
    const currentIndex = comicsToUse.findIndex(comic => comic.id === selectedComic.id);
    if (currentIndex === -1) return;
    
    let targetIndex;
    if (direction === 'prev') {
      // Previous = go to previous comic (lower episode number)
      targetIndex = currentIndex < comicsToUse.length - 1 ? currentIndex + 1 : 0;
    } else {
      // Next = go to next comic (higher episode number)  
      targetIndex = currentIndex > 0 ? currentIndex - 1 : comicsToUse.length - 1;
    }
    
    const targetComic = comicsToUse[targetIndex];
    if (targetComic) {
      // Just update the URL - the useEffect will handle the comic change
      const newUrl = `/daily-circus?slug=${targetComic.slug}`;
      navigate(newUrl, { replace: true });
    }
  };

  

  const scrollToTodaysComic = () => {
    const element = document.getElementById('todays-comic');
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <>
      {seoData && (
        <SEO 
          data={{
            ...seoData,
            ...(getDynamicComicData() || {})
          }} 
          canonicalUrl={canonicalUrl}
        />
      )}
      
      <div className="min-h-screen">
        <Navigation />
        
        {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24">
        {/* Comic-inspired background */}
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-100 via-pink-50 to-blue-100">
          {/* Animated comic shapes */}
          <motion.div
            animate={{ 
              rotate: [0, 360],
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute top-10 left-10 w-20 h-20 rounded-full bg-gradient-to-r from-yellow-300 to-orange-300 opacity-20"
          />
          <motion.div
            animate={{ 
              rotate: [360, 0],
              y: [0, -20, 0]
            }}
            transition={{ 
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-1/4 right-20 w-16 h-16 bg-gradient-to-r from-pink-300 to-purple-300 opacity-20 transform rotate-45"
          />
          <motion.div
            animate={{ 
              x: [0, 30, 0],
              rotate: [0, 180, 360]
            }}
            transition={{ 
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute bottom-20 left-1/4 w-12 h-12 rounded-full bg-gradient-to-r from-blue-300 to-cyan-300 opacity-20"
          />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto text-center space-y-8">
            {/* Comic-style speech bubble title */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative inline-block"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="relative bg-white rounded-full px-8 py-4 shadow-lg border-4 border-gray-800"
              >
                <h1 className="text-4xl md:text-6xl font-bold text-gray-800 relative z-10">
                  d(AI)ly Circus
                </h1>
                {/* Speech bubble tail */}
                <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[20px] border-r-[20px] border-t-[20px] border-l-transparent border-r-transparent border-t-gray-800"></div>
                <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[16px] border-r-[16px] border-t-[16px] border-l-transparent border-r-transparent border-t-white"></div>
              </motion.div>
            </motion.div>


            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="relative"
            >
              {/* Another speech bubble for description */}
              <div className="bg-gradient-to-r from-yellow-200 to-orange-200 rounded-2xl px-8 py-6 shadow-lg border-3 border-gray-600 max-w-4xl mx-auto">
                <p className="text-xl md:text-2xl text-gray-800 font-medium">
                  Your daily comic strip where AI and marketing steal the spotlight!
                  Join our colorful cast as they navigate the wonderful chaos of the digital age.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4"
            >
              <Button 
                size="lg" 
                onClick={scrollToTodaysComic}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-3 px-8 rounded-full shadow-lg transform hover:scale-105 transition-all duration-200"
              >
                🎪 Read Today's Comic
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                onClick={() => {
                  const element = document.getElementById('archive');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="border-3 border-gray-600 text-gray-800 font-bold py-3 px-8 rounded-full shadow-lg hover:bg-gray-100 transform hover:scale-105 transition-all duration-200"
              >
                📚 Browse Archive
              </Button>
            </motion.div>

            {/* Marquee of Comics */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.0, duration: 0.6 }}
              className="mt-16 relative"
            >
              <div className="w-full max-w-6xl mx-auto">
                <Marquee speed="slow" className="py-8">
                  {filteredComics.slice(0, 6).map((comic) => (
                    <div key={comic.id} className="mx-4 cursor-pointer group/card" onClick={() => handleComicClick(comic)}>
                      <Card className="w-64 overflow-hidden hover:shadow-lg transition-shadow duration-200 border-2 border-gray-300">
                        <CardContent className="p-0">
                          <div className="aspect-video overflow-hidden relative">
                            <img 
                              src={comic.imageUrl} 
                              alt={comic.title}
                              className="w-full h-auto object-cover object-top scale-125 group-hover/card:scale-[1.35] transition-transform duration-200"
                              style={{ minHeight: '200%' }}
                            />
                            {/* View Comic button on hover */}
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/card:opacity-100 transition-opacity duration-200">
                              <div className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium shadow-lg transform translate-y-2 group-hover/card:translate-y-0 transition-transform duration-200">
                                View Comic
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ))}
                </Marquee>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Today's Comic */}
      <section id="todays-comic" className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Today's Comic</h2>
            <p className="text-lg text-muted-foreground">Fresh from the d(AI)ly Circus archives</p>
          </div>
          
          {/* Today's Comic with Previous Button inline */}
          {todaysComic ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Badge variant="secondary" className="flex items-center gap-2">
                    <Hash className="w-4 h-4" />
                    Episode {todaysComic.episode}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="flex items-center gap-2"
                    onClick={() => {
                      // Find the last comic (highest episode number)
                      const lastComic = comicsToUse.reduce((latest, current) => 
                        current.episode > latest.episode ? current : latest
                      );
                      handleComicClick(lastComic);
                    }}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                </div>
              </div>
              
              <ComicViewer comic={todaysComic} showEpisodeBadge={false} onShare={handleShare} />
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-lg text-muted-foreground">No comics found for this filter.</p>
            </div>
          )}
        </div>
      </section>

      {/* Archive Gallery */}
      <section id="archive" className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Comic Archive</h2>
            <p className="text-lg text-muted-foreground">Browse through all our hilarious episodes</p>
          </div>
          <ComicGrid comics={filteredComics} onComicClick={handleComicClick} onTabChange={handleTabChange} />
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold mb-8">About d(AI)ly Circus</h2>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="space-y-4 text-left">
              <p className="text-lg">
                Welcome to the d(AI)ly Circus, where artificial intelligence meets marketing mayhem in the most entertaining way possible!
              </p>
              <p>
                Our daily comic strip captures the hilarious reality of working with AI in marketing, featuring our beloved characters navigating the ups and downs of the digital age.
              </p>
              <div className="grid grid-cols-3 gap-4 mt-8">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{filteredComics.length}</div>
                  <div className="text-sm text-muted-foreground">Comics Published</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">{laughsGenerated}</div>
                  <div className="text-sm text-muted-foreground">Laughs Generated</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">Daily</div>
                  <div className="text-sm text-muted-foreground">New Episodes</div>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <Card className="w-64 overflow-hidden">
                <CardContent className="p-0">
                  <AspectRatio ratio={1} className="bg-muted">
                    <img 
                      src={dailyCircusBotImage} 
                      alt="d(AI)ly Circus robot character"
                      className="w-full h-full object-cover"
                    />
                  </AspectRatio>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-2xl text-center">
          <h2 className="text-4xl font-bold mb-8">Join the Circus Community</h2>
          <div className="space-y-6">
            <p className="text-lg text-muted-foreground">
              Don't miss a punchline! Get d(AI)ly Circus delivered to your LinkedIn feed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <LinkedInSubscribeButton />
              <Button variant="outline" size="lg" asChild>
                <Link to="/contact">Suggest a Story</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <FooterWithNewsletter />

      {/* Comic Modal */}
      <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent onPointerDownOutside={(e) => {
          // Prevent closing when clicking on share sidebar
          const target = e.target as Element;
          if (target.closest('[data-share-sidebar]')) {
            e.preventDefault();
          }
        }}>
          {selectedComic && (
            <ComicViewer 
              comic={selectedComic}
              onPrevious={() => navigateComic('prev')}
              onNext={() => navigateComic('next')}
              hasPrevious={true}
              hasNext={true}
              onShare={handleShare}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Share Sidebar - Only shows when popup is open */}
      <ShareSidebar
        isOpen={isDialogOpen}
        shareUrl={`${window.location.origin}/daily-circus?slug=${selectedComic?.slug || `comic-episode${selectedComic?.episode}`}`}
        shareTitle={selectedComic?.title || 'Daily Circus Comic'}
        shareDescription={selectedComic?.description || 'Check out this hilarious comic from d(AI)ly Circus!'}
      />
    </div>
    </>
  );
};

export default DailyCircusPage;